/**
 * Secondary Users Module
 *
 * Manages secondary/tenant users scoped to a primary user (super_user_id).
 * Application access is stored per-user in checkpoint_secondary_user_apps.
 */

import { HttpClient } from './http-client';
import { CheckpointConfig } from './types';
import { DEFAULT_CHECKPOINT_APP, type CheckpointAppName } from './app-names';

export interface SecondaryUserAppAccess {
  role: string | null;
  permissions: string[];
}

/**
 * Secondary user data
 */
export interface SecondaryUser {
  id: number;
  super_user_id: number;
  email: string;
  username: string;
  email_verified: boolean;
  status: number;
  metadata: Record<string, unknown>;
  apps: Record<string, SecondaryUserAppAccess>;
  app_names: CheckpointAppName[];
  /** App the user authenticated into (login context). */
  app_name?: CheckpointAppName;
  last_login?: string;
  created_at: string;
}

export interface SecondaryUserAppGrant {
  role?: string;
  permissions?: string[];
}

/**
 * Secondary user registration result
 */
export interface SecondaryUserRegistration {
  user_id: number;
  super_user_id: number;
  email: string;
  username: string;
  apps: CheckpointAppName[];
  message: string;
}

/**
 * Secondary user login result
 */
export interface SecondaryUserAuth {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  app_name: CheckpointAppName;
  user: SecondaryUser;
  member?: Record<string, unknown> | null;
  identifier?: string | null;
  login_identifier?: string | null;
}

export interface SecondaryIdentifier {
  id: number;
  super_user_id: number;
  identifier: string;
  created_at: string;
  updated_at?: string | null;
}

export interface SecondaryIdentifierStatus {
  has_identifier: boolean;
  identifier: string | null;
  primary_username: string | null;
}

export interface SecondaryIdentifierCheck {
  identifier: string;
  valid_format: boolean;
  available: boolean;
}

export interface SecondaryUserAccountOption {
  super_user_id: number;
  email: string;
  username: string;
  last_login?: string;
}

export interface SecondaryUserMultipleAccounts {
  multiple_accounts: true;
  accounts: SecondaryUserAccountOption[];
  selection_token: string;
  message: string;
}

export type SecondaryUserLoginResult = SecondaryUserAuth | SecondaryUserMultipleAccounts;

export interface SecondaryUsersList {
  users: SecondaryUser[];
  total: number;
  limit: number;
  offset: number;
}

export class SecondaryUsers {
  private http: HttpClient;
  private config: CheckpointConfig;
  private currentUser: SecondaryUser | null = null;

  constructor(http: HttpClient, config: CheckpointConfig) {
    this.http = http;
    this.config = config;
  }

  private resolveAppName(appName?: CheckpointAppName): CheckpointAppName {
    return appName ?? this.config.appName ?? DEFAULT_CHECKPOINT_APP;
  }

  /**
   * Register a secondary user with access to one or more applications.
   */
  async register(data: {
    email: string;
    password: string;
    username: string;
    apps: Record<string, SecondaryUserAppGrant>;
    metadata?: Record<string, unknown>;
  }): Promise<SecondaryUserRegistration> {
    return this.http.post<SecondaryUserRegistration>(
      '/secondary/register',
      data,
      true
    );
  }

  /**
   * Login as a secondary user for a specific application context.
   */
  async login(
    credentials: {
      identifier: string;
      password: string;
    },
    options?: {
      appName?: CheckpointAppName;
      superUserId?: number;
    }
  ): Promise<SecondaryUserLoginResult> {
    const appName = this.resolveAppName(options?.appName);
    const superUserId = options?.superUserId;

    const body: Record<string, unknown> = {
      app_name: appName,
      ...credentials,
      ...(superUserId !== undefined && { super_user_id: superUserId }),
    };

    const response = await this.http.post<SecondaryUserLoginResult>(
      '/secondary/login',
      body
    );

    if ('multiple_accounts' in response && response.multiple_accounts) {
      return response;
    }

    const authResponse = response as SecondaryUserAuth;
    await this.http.setTokens({
      access_token: authResponse.access_token,
      refresh_token: authResponse.refresh_token,
      expires_in: authResponse.expires_in,
      token_type: authResponse.token_type,
      refresh_expires_in: authResponse.expires_in * 2,
    });

    this.currentUser = authResponse.user;
    return authResponse;
  }

  isMultipleAccountsResponse(result: SecondaryUserLoginResult): result is SecondaryUserMultipleAccounts {
    return 'multiple_accounts' in result && result.multiple_accounts === true;
  }

  async selectAccount(selectionToken: string, superUserId: number): Promise<SecondaryUserAuth> {
    const response = await this.http.post<SecondaryUserAuth>(
      '/secondary/select-account',
      {
        selection_token: selectionToken,
        super_user_id: superUserId,
      }
    );

    await this.http.setTokens({
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      expires_in: response.expires_in,
      token_type: response.token_type,
      refresh_expires_in: response.expires_in * 2,
    });

    this.currentUser = response.user;
    return response;
  }

  getCurrentUser(): SecondaryUser | null {
    return this.currentUser;
  }

  async list(
    options?: { appName?: CheckpointAppName; limit?: number; offset?: number }
  ): Promise<SecondaryUsersList> {
    const params = new URLSearchParams();
    if (options?.appName) params.set('app_name', options.appName);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    return this.http.get<SecondaryUsersList>(
      `/secondary/users${query ? `?${query}` : ''}`,
      true
    );
  }

  async get(userId: number): Promise<SecondaryUser> {
    return this.http.get<SecondaryUser>(`/secondary/user/${userId}`, true);
  }

  async update(
    userId: number,
    data: Partial<{
      email: string;
      username: string;
      password: string;
      status: number;
      metadata: Record<string, unknown>;
      apps: Record<string, SecondaryUserAppGrant>;
    }>
  ): Promise<SecondaryUser> {
    return this.http.put<SecondaryUser>(`/secondary/user/${userId}`, data, true);
  }

  async delete(userId: number): Promise<void> {
    await this.http.delete(`/secondary/user/${userId}`, true);
  }

  async getIdentifierStatus(): Promise<SecondaryIdentifierStatus> {
    return this.http.get<SecondaryIdentifierStatus>('/secondary/identifier', true);
  }

  async createIdentifier(data: {
    identifier?: string;
    use_primary_username?: boolean;
  }): Promise<SecondaryIdentifier> {
    return this.http.post<SecondaryIdentifier>('/secondary/identifier', data, true);
  }

  async checkIdentifier(identifier: string): Promise<SecondaryIdentifierCheck> {
    const params = new URLSearchParams({ identifier });
    return this.http.get<SecondaryIdentifierCheck>(
      `/secondary/identifier/check?${params.toString()}`
    );
  }

  async validateToken(token: string): Promise<{
    valid: boolean;
    user?: SecondaryUser;
    member?: Record<string, unknown> | null;
    super_user_id?: number;
    error?: string;
  }> {
    return this.http.post<{
      valid: boolean;
      user?: SecondaryUser;
      member?: Record<string, unknown> | null;
      super_user_id?: number;
      error?: string;
    }>(
      '/secondary/token/validate',
      { token }
    );
  }
}
